import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bike, Clock, Clock3, HelpCircle, LogOut, Menu, MapPin, Milk, Phone, X, RotateCcw,
  CheckCircle2, XCircle, Truck,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  DELIVERY_MODE, ORDER_STATUS, PAYMENT_METHOD_LABELS, formatSlotWindow, sortByUrgency,
} from '../../api/orders';
import { listDeliveryOrders, startDelivery, DELIVERY_ERRORS } from '../../api/delivery';
import {
  listDriverReturnTasks,
  RETURN_STATUS_LABELS,
  RETURN_TYPE,
  RETURN_TYPE_LABELS,
} from '../../api/returns';
import {
  DROP_STATUS,
  DROP_STATUS_LABELS,
  dateInIst,
  deliverDrop,
  failDrop,
  listDrops,
  startDrop,
} from '../../api/subscriptions';
import { GROUP_STATUS_MIXED, groupReturnsByOrderId } from '../../utils/returnGroups';
import { formatCurrency, timeAgo } from '../../data/mockData';
import Avatar from '../../components/Avatar';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import Button from '../../components/Button';
import DriverShell from './DriverShell';
import bhimaLogo from '../../assets/bhima-logo.svg';
import welcomeBoard from '../../assets/welcome board.png';
import styles from './DriverHome.module.css';

const NAV_ITEMS = [
  { icon: Clock3, label: 'Ride history', path: '/driver/history' },
  { icon: HelpCircle, label: 'Support', path: '/driver/support' },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function friendlyError(err) {
  return DELIVERY_ERRORS[err.message] || err.message || 'Something went wrong.';
}

export default function DriverHome() {
  const { user, logout } = useAuth();
  const { notify } = useOrders();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeRides, setActiveRides] = useState([]);
  const [returnTasks, setReturnTasks] = useState([]);
  const [subsDrops, setSubsDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const pickupGroups = useMemo(() => groupReturnsByOrderId(returnTasks), [returnTasks]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rides, returns, dropsPayload] = await Promise.all([
        listDeliveryOrders(`${ORDER_STATUS.PACKED},${ORDER_STATUS.OUT_FOR_DELIVERY}`, user.idToken),
        listDriverReturnTasks(user.idToken),
        listDrops(dateInIst(0), user.idToken).catch(() => ({ drops: [] })),
      ]);
      setActiveRides(sortByUrgency(rides));
      setReturnTasks(returns);
      // Subscriptions never appear on grocery Delivery — show drops assigned to this rider.
      const mine = (dropsPayload.drops || []).filter(
        (d) => d.deliveryPersonSub === user.sub
          && (d.status === DROP_STATUS.PENDING || d.status === DROP_STATUS.OUT),
      );
      setSubsDrops(mine);
    } catch (err) {
      setError(err.message || 'Could not load your tasks.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken, user.sub]);

  useEffect(() => {
    load();
  }, [load]);

  const firstName = (user?.name || 'Driver').split(' ')[0];

  const handleStart = async (order) => {
    setBusyId(order.id);
    try {
      await startDelivery(order.id, user.idToken);
      notify(`Order ${order.id} is now out for delivery`);
      await load();
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  const withDropBusy = async (dropId, action, successMessage) => {
    setBusyId(dropId);
    try {
      await action();
      if (successMessage) notify(successMessage);
      await load();
    } catch (err) {
      notify(err.message || 'Could not update this drop.');
    } finally {
      setBusyId(null);
    }
  };

  const handleStartDrop = (drop) => withDropBusy(
    drop.id,
    () => startDrop(drop.id, user.idToken),
    'Subscription drop started',
  );

  const handleDeliverDrop = (drop) => withDropBusy(
    drop.id,
    () => deliverDrop(drop.id, user.idToken),
    'Marked delivered',
  );

  const handleFailDrop = (drop) => {
    const reason = window.prompt('Reason for failed delivery?');
    if (reason == null) return;
    withDropBusy(
      drop.id,
      () => failDrop(drop.id, reason.trim() || undefined, user.idToken),
      'Marked failed',
    );
  };

  const totalTasks = activeRides.length + pickupGroups.length + subsDrops.length;

  return (
    <DriverShell>
      <header className={styles.header}>
        <button className={styles.iconBtn} aria-label="Open menu" onClick={() => setMenuOpen(true)}>
          <Menu size={19} />
        </button>

        <div className={styles.brand}>
          <img src={bhimaLogo} alt="Bhima Store" className={styles.brandLogo} />
          <span className={styles.brandSub}>Delivery Partner</span>
        </div>
      </header>

      <div className={styles.hero}>
        <img src={welcomeBoard} alt="" className={styles.heroImage} />
        <div className={styles.heroText}>
          <p className={styles.heroGreeting}>
            {greeting()},<br />
            <span className={styles.heroName}>{firstName}!</span> <span role="img" aria-label="wave">👋</span>
          </p>
          <p className={styles.heroTagline}>Let's deliver smiles today</p>
        </div>
      </div>

      <main className={styles.content}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Active Tasks & Pickups</h2>
          {totalTasks > 0 && <span className={styles.sectionCount}>{totalTasks}</span>}
        </div>

        {loading && <p>Loading your rides…</p>}

        {!loading && error && (
          <EmptyState
            icon={Bike}
            title="Could not load your rides"
            description={error}
            action={<Button variant="outline" onClick={load}>Retry</Button>}
          />
        )}

        {!loading && !error && totalTasks === 0 && (
          <EmptyState
            icon={Bike}
            title="No active tasks"
            description="New grocery deliveries, subscription drops, or return pickups assigned to you will show up here."
          />
        )}

        {!loading && !error && totalTasks > 0 && (
          <div className={styles.rideList}>
            {/* Morning subscription drops assigned to this rider */}
            {subsDrops.map((drop) => (
              <div
                className={styles.rideCard}
                key={drop.id}
                style={{ borderLeft: '4px solid #B8850A' }}
              >
                <div className={styles.rideTop}>
                  <span className={styles.rideId} style={{ color: '#B8850A', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Milk size={14} />
                    Subscription · {drop.orderId || drop.id}
                  </span>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 10,
                    background: '#FDF2D3',
                    color: '#B8850A',
                  }}
                  >
                    {DROP_STATUS_LABELS[drop.status] || drop.status}
                  </span>
                </div>

                <p className={styles.rideAddress}>
                  <strong>{drop.itemName || 'Item'}</strong>
                  {' '}
                  ×
                  {drop.qty}
                  {drop.slotLabel ? ` · ${drop.slotLabel}` : ''}
                </p>
                {(drop.customerName || drop.customerPhone) && (
                  <p className={styles.ridePlaced} style={{ marginTop: 4 }}>
                    {drop.customerName || 'Customer'}
                    {drop.customerPhone ? (
                      <>
                        {' · '}
                        <a href={`tel:${drop.customerPhone}`} style={{ color: 'inherit' }}>
                          <Phone size={11} style={{ verticalAlign: 'middle' }} />
                          {' '}
                          {drop.customerPhone}
                        </a>
                      </>
                    ) : null}
                  </p>
                )}
                {drop.address && (
                  <p className={styles.rideAddress}>
                    <MapPin size={12} />
                    {' '}
                    {drop.address}
                  </p>
                )}

                <div className={styles.rideActions} style={{ marginTop: 10 }}>
                  {drop.status === DROP_STATUS.PENDING && (
                    <Button
                      size="sm"
                      variant="primary"
                      icon={Truck}
                      disabled={busyId === drop.id}
                      onClick={() => handleStartDrop(drop)}
                    >
                      {busyId === drop.id ? 'Starting…' : 'Start drop'}
                    </Button>
                  )}
                  {drop.status === DROP_STATUS.OUT && (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        icon={CheckCircle2}
                        disabled={busyId === drop.id}
                        onClick={() => handleDeliverDrop(drop)}
                      >
                        Delivered
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        icon={XCircle}
                        disabled={busyId === drop.id}
                        onClick={() => handleFailDrop(drop)}
                      >
                        Fail
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Return / exchange pickups — one card per order */}
            {pickupGroups.map((group) => {
              const isBulk = group.isBulk;
              const returnCount = group.items.filter((i) => i.type === RETURN_TYPE.RETURN_CREDIT).length;
              const exchangeCount = group.items.length - returnCount;
              const statusText = group.status === GROUP_STATUS_MIXED
                ? 'Pickup Scheduled'
                : (RETURN_STATUS_LABELS[group.status] || group.status);

              return (
              <div className={styles.rideCard} key={group.orderId} style={{ borderLeft: '4px solid #e11d48' }}>
                <div className={styles.rideTop}>
                  <span className={styles.rideId} style={{ color: '#e11d48', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <RotateCcw size={14} />
                    {isBulk ? `Return Pickup · Order ${group.orderId}` : `Return Pickup #${group.items[0].id}`}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '10px', background: '#ffe4e6', color: '#be123c' }}>
                    {statusText}
                  </span>
                </div>

                <p className={styles.rideAddress}>
                  Customer: <strong>{group.customerName}</strong> ({group.customerPhone})
                </p>
                {isBulk && (
                  <p className={styles.ridePlaced} style={{ marginTop: '4px' }}>
                    {group.items.length} items
                    {returnCount > 0 && exchangeCount > 0
                      ? ` · ${returnCount} return${returnCount !== 1 ? 's' : ''}, ${exchangeCount} exchange${exchangeCount !== 1 ? 's' : ''}`
                      : returnCount > 0
                        ? ` · ${returnCount} return${returnCount !== 1 ? 's' : ''}`
                        : ` · ${exchangeCount} exchange${exchangeCount !== 1 ? 's' : ''}`}
                  </p>
                )}

                <div className={styles.rideBottom}>
                  <span className={styles.rideItems}>
                    {isBulk ? (
                      <>
                        Collect: {group.items.map((i) => i.productName).slice(0, 2).join(', ')}
                        {group.items.length > 2 ? ` +${group.items.length - 2} more` : ''}
                      </>
                    ) : (
                      <>Collect: {group.items[0].productName} (Qty {group.items[0].qty})</>
                    )}
                  </span>
                  <span className={styles.rideTotal}>₹{group.totalCredit}</span>
                </div>
                {isBulk && (
                  <p className={styles.ridePlaced} style={{ marginTop: '4px', fontSize: '11px' }}>
                    {group.items.map((i) => `${i.id} (${RETURN_TYPE_LABELS[i.type]})`).join(' · ')}
                  </p>
                )}

                <div className={styles.rideActions} style={{ marginTop: '10px' }}>
                  <Button
                    size="sm"
                    variant="primary"
                    style={{ background: '#e11d48', borderColor: '#e11d48', color: '#ffffff' }}
                    onClick={() => navigate(`/driver/returns/order/${group.orderId}`)}
                  >
                    {isBulk ? `Collect Pickup (${group.items.length} items)` : 'Collect Pickup'}
                  </Button>
                </div>
              </div>
              );
            })}

            {/* Render Standard Delivery Rides */}
            {activeRides.map((order) => {
              const isPacked = order.status === ORDER_STATUS.PACKED;
              return (
                <div className={styles.rideCard} key={order.id}>
                  <div className={styles.rideTop}>
                    <span className={styles.rideId}>{order.id}</span>
                    <StatusBadge status={order.status} size="sm" />
                  </div>

                  {order.deliveryMode === DELIVERY_MODE.SCHEDULED && (
                    <p className={styles.rideSchedule}>
                      <Clock size={11} /> {formatSlotWindow(order.slotStart, order.slotEnd) || 'Scheduled'}
                    </p>
                  )}

                  {order.address && (
                    <p className={styles.rideAddress}>
                      <MapPin size={12} /> {order.address}
                    </p>
                  )}

                  <div className={styles.rideBottom}>
                    <span className={styles.rideItems}>
                      {order.items.length} item{order.items.length !== 1 ? 's' : ''} · {PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}
                    </span>
                    <span className={styles.rideTotal}>{formatCurrency(order.total)}</span>
                  </div>

                  {order.placedAt && <p className={styles.ridePlaced}>Placed {timeAgo(order.placedAt)}</p>}

                  <div className={styles.rideActions}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/driver/rides/${order.id}`, { state: { order } })}
                    >
                      View run
                    </Button>
                    {isPacked ? (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={busyId === order.id}
                        onClick={() => handleStart(order)}
                      >
                        {busyId === order.id ? 'Starting…' : 'Start delivery'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => navigate(`/driver/rides/${order.id}/proof`, { state: { order } })}
                      >
                        Give delivery
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {menuOpen && (
        <div className={styles.overlay} onMouseDown={() => setMenuOpen(false)}>
          <aside className={styles.drawer} onMouseDown={(e) => e.stopPropagation()}>
            <div className={styles.drawerHeader}>
              <Avatar name={user?.name || 'Delivery Partner'} size={44} />
              <button className={styles.closeBtn} aria-label="Close menu" onClick={() => setMenuOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <p className={styles.drawerName}>{user?.name}</p>
            <p className={styles.drawerEmail}>{user?.email}</p>

            <nav className={styles.drawerNav}>
              {NAV_ITEMS.map(({ icon: Icon, label, path }) => (
                <button
                  type="button"
                  key={label}
                  className={styles.drawerRow}
                  disabled={!path}
                  onClick={path ? () => { setMenuOpen(false); navigate(path); } : undefined}
                >
                  <Icon size={17} />
                  {label}
                </button>
              ))}
            </nav>

            <button
              className={styles.logoutBtn}
              onClick={() => { logout(); navigate('/login'); }}
            >
              <LogOut size={16} /> Sign out
            </button>
          </aside>
        </div>
      )}
    </DriverShell>
  );
}
